import { Component, input, model, effect, signal, computed, inject, ChangeDetectionStrategy, output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormMode } from 'app/core/constants/form-mode';
import { VehicleDamageSelectorComponent } from '../../../management-vehicle/vehicle-damage-selector/vehicle-damage-selector.component';
import { formatVehiclePartLabel } from '../../../management-vehicle/vehicle-damage-selector/vehicle-damage-parts';
import { LocationPickerComponent } from 'app/core/components/location-picker/location-picker.component';
import { CatalogService } from '@services/data/catalog.service';
import { NumberFormatDirective } from 'app/core/directives/number-format.directive';
import { formatDisplayNumber, sanitizeNumericValue } from '../../utils/claim-details.util';
import { noticeMaxDays } from '../../utils/claim-process-rules.util';

@Component({
  selector: 'app-claim-detail-form',
  templateUrl: './claim-detail-form.component.html',
  styleUrls: ['./claim-detail-form.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, VehicleDamageSelectorComponent, LocationPickerComponent, DatePipe, NumberFormatDirective]
})
export class ClaimDetailFormComponent {
  mode = input<FormMode>(FormMode.VIEW);
  detail = model<any>();
  partyData = input<any>();
  selectedVehicleType = input<string>('sedan');
  /** @deprecated Deducible vive en el claim; se mantiene por compatibilidad de inputs. */
  deductiblePercentages = input<{ normalPercentage: number, negligentPercentage: number, totalLossPercentage?: number }>({ normalPercentage: 10, negligentPercentage: 30, totalLossPercentage: 15 });
  isAdminMode = input<boolean>(false);
  canEditDetails = input<boolean>(false);
  /** BPM: revisor puede marcar negligencia en esta línea. */
  canEditNegligence = input<boolean>(false);
  isTotalLoss = input<boolean>(false);
  vehicleValue = input<number>(0);
  processSlug = input<string | null>(null);
  partsSelected = output<string[]>();
  negligenceChange = output<boolean>();

  private catalogService = inject(CatalogService);
  noticeDays = computed(() => noticeMaxDays(this.catalogService, this.processSlug()));
  formMode = FormMode;
  maxDate = signal<string>(new Date().toISOString().substring(0, 10));
  minDate = computed(() => {
    const d = new Date();
    d.setDate(d.getDate() - this.noticeDays());
    return d.toISOString().substring(0, 10);
  });

  /** Límites de texto para reseña y daños físicos */
  readonly descriptionMin = 30;
  readonly descriptionMax = 1000;
  readonly damageDescriptionMin = 10;
  readonly damageDescriptionMax = 500;

  description = signal<string>('');
  damageDescription = signal<string>('');
  geographicLocation = signal<string>('');
  latitude = signal<number | null>(null);
  longitude = signal<number | null>(null);
  laborCost = signal<number>(0);
  partsCost = signal<number>(0);
  laborCostDisplay = signal<string>('0');
  partsCostDisplay = signal<string>('0');
  dateOfLoss = signal<string>('');
  isDriverSameAsParty = signal<boolean>(true);
  driverName = signal<string>('');
  driverLicense = signal<string>('');
  driverLicenseClass = signal<string>('');
  affectedParts = signal<string[]>([]);
  isNegligence = signal<boolean>(false);
  deductible = signal<number>(0);

  get canToggleNegligence(): boolean {
    return this.canEditNegligence() || this.isAdminMode();
  }

  private lastProcessedDetail: any = null;

  constructor() {
    effect(() => {
      const det = this.detail();
      if (det && det !== this.lastProcessedDetail) {
        this._populateFields(det);
      }
    }, { allowSignalWrites: true });
  }

  private _populateFields(det: any) {
    this.lastProcessedDetail = det;
    this.description.set(det.description ?? '');
    this.damageDescription.set(det.damageDescription ?? '');
    this.geographicLocation.set(det.geographicLocation ?? '');
    this.latitude.set(this._toCoord(det.latitude));
    this.longitude.set(this._toCoord(det.longitude));
    this.laborCost.set(sanitizeNumericValue(det.laborCost));
    this.partsCost.set(sanitizeNumericValue(det.partsCost));
    this.laborCostDisplay.set(formatDisplayNumber(this.laborCost()));
    this.partsCostDisplay.set(formatDisplayNumber(this.partsCost()));
    this._populateRemainingFields(det);
  }

  private _populateRemainingFields(det: any) {
    this.isNegligence.set(det.isNegligence ?? false);
    this.deductible.set(Number(det.deductible ?? 0));
    this.dateOfLoss.set(det.dateOfLoss ?? '');
    const parts = Array.isArray(det.affectedParts) ? det.affectedParts : this.parseJSON(det.affectedParts);
    this.affectedParts.set(parts);
    this._syncDriverFields(det);
    if (this.isTotalLoss()) {
      this.updateModel();
    }
  }

  onNegligenceToggle(value: boolean) {
    this.isNegligence.set(value);
    this.updateModel();
    this.negligenceChange.emit(value);
  }

  private _syncDriverFields(det: any) {
    const party = this.partyData() ?? {};
    const isSame = !det.driverLicense || det.driverLicense === party.rut;
    this.isDriverSameAsParty.set(isSame);
    this.driverName.set(isSame ? '' : (det.driverName ?? ''));
    this.driverLicense.set(isSame ? '' : (det.driverLicense ?? ''));
    this.driverLicenseClass.set(isSame ? '' : (det.driverLicenseClass ?? ''));
  }

  onDriverSameChange() {
    if (this.isDriverSameAsParty()) {
      this.driverName.set('');
      this.driverLicense.set('');
      this.driverLicenseClass.set('');
    }
    this.updateModel();
  }

  onMoneyChange(field: 'labor' | 'parts', value: string) {
    const amount = sanitizeNumericValue(value);
    if (field === 'labor') {
      this.laborCostDisplay.set(value);
      this.laborCost.set(amount);
    } else {
      this.partsCostDisplay.set(value);
      this.partsCost.set(amount);
    }
    this.updateModel();
  }

  updateModel() {
    const labor = sanitizeNumericValue(this.laborCost());
    const parts = sanitizeNumericValue(this.partsCost());
    let total = labor + parts;
    let computedDeductible = 0;

    if (this.isTotalLoss()) {
      total = this.vehicleValue();
      const pct = Number(this.deductiblePercentages().totalLossPercentage ?? 15);
      computedDeductible = Math.round(total * (pct / 100));
    } else {
      const pct = this.isNegligence()
        ? this.deductiblePercentages().negligentPercentage
        : this.deductiblePercentages().normalPercentage;
      computedDeductible = Math.round(total * (pct / 100));
    }
    this.deductible.set(computedDeductible);
    this._propagateDetailChange(total, computedDeductible);
  }

  private _propagateDetailChange(total: number, computedDeductible: number) {
    const updatedDetail = {
      ...this.detail(),
      description: this.description(),
      damageDescription: this.damageDescription(),
      geographicLocation: this.geographicLocation(),
      latitude: this.latitude(),
      longitude: this.longitude(),
      laborCost: sanitizeNumericValue(this.laborCost()),
      partsCost: sanitizeNumericValue(this.partsCost()),
      totalCost: total,
      isNegligence: this.isNegligence(),
      deductible: computedDeductible,
      dateOfLoss: this.dateOfLoss(),
      driverName: this._getDriverName(),
      driverLicense: this._getDriverLicense(),
      affectedParts: this.affectedParts(),
      driverLicenseClass: this.isDriverSameAsParty() ? null : this.driverLicenseClass()
    };
    this.lastProcessedDetail = updatedDetail;
    this.detail.set(updatedDetail);
  }

  private _getDriverName(): string {
    const party = this.partyData() ?? {};
    return this.isDriverSameAsParty()
      ? `${party.names ?? ''} ${party.lastNames ?? ''}`.trim()
      : this.driverName();
  }

  private _getDriverLicense(): string {
    return this.isDriverSameAsParty()
      ? (this.partyData()?.rut ?? '')
      : this.driverLicense();
  }

  onPartsSelected(parts: string[]) {
    this.affectedParts.set(parts);
    this.partsSelected.emit(parts);
    this.updateModel();
  }

  onLocationChange(loc: { address: string; latitude: number | null; longitude: number | null }) {
    const address = (loc.address ?? '').slice(0, 255);
    this.geographicLocation.set(address);
    this.latitude.set(loc.latitude);
    this.longitude.set(loc.longitude);
    this.updateModel();
  }

  openLocationOnMap() {
    const lat = this.latitude();
    const lng = this.longitude();
    if (lat == null || lng == null) return;
    const url = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private _toCoord(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  parseJSON(val: string): string[] {
    try {
      return JSON.parse(val || '[]');
    } catch {
      return [];
    }
  }

  formatPartName(part: string): string {
    return formatVehiclePartLabel(part, this.selectedVehicleType());
  }
}
